import _ from 'lodash';
import { 
  APP_SETTINGS, 
  AuthRoutesConfig
} from '../../authentication/services/AuthSettings';
import { Paths } from '../../authentication/interfaces/AuthInterface';
import CookieUtil from '../../../utility/CookieUtil';
import Configuration from '../../config/Configuration';
import { COOKIE } from '../../config/Constants';
import * as ThemeActions from '../../../actions/sevices/theme/ThemeService';
import { Service } from '../Service';
import { iThemeProviderProps } from './Types';

class ThemeService extends Service {
  private defaultThemeName: string = 'primavera-default';
  private skipLoadingThemeForAuthRoutes: string[] = [];
  private resolver: any;
  private reject: any;
  private themes: iThemeProviderProps;
  private previousTheme: string = null;
  private initialized: boolean = false;

  /**
   * @inheritDoc
   */
  constructor() {
    super();
    this.skipLoadingThemeForAuthRoutes = AuthRoutesConfig
      .map((item: Paths) => item.path as string)
      .filter((path: string) => ![APP_SETTINGS.login_uri, APP_SETTINGS.post_logout_redirect_uri].includes(path));

    this.registerServiceToDispatcher(ThemeActions);
    this.previousTheme = CookieUtil.getTheme();
  }

  /**
   * Init themes
   * @param {any} themes
   * @param {string} theme Force theme or load first theme if this param is not set
   */
  init = (themes, theme = null) => {
    this.setThemes(themes);
    if (!this.initialized) {
      theme = theme ? theme : CookieUtil.getTheme() as string;
      let themeData = this.getThemeData(theme);
      this.loadStylesheet(themeData.theme);
    }
  }

  /**
   * Clear all stylesheets from the page which are not loaded with the current theme link
   * @param {string} theme Link theme name
   */
  clearStylesheets = (theme: string) => {
    const head = document.querySelector('head') as HTMLHeadElement;
    const themeToRemove = head.querySelector(`link[rel=stylesheet]:not([href$="/${theme}.css"])`);
    // get link with rel="stylesheet" which href attribute doesn't end with `/${theme}.css`
    if (themeToRemove && themeToRemove.parentElement) {
      themeToRemove.parentElement.removeChild(themeToRemove);
    }
  };

  /**
   * Resolves theme loading promise and clears all old theme links
   * @param {string} theme Link theme name
   */
  changeThemeResolve = (theme: string | undefined = undefined) => {
    theme && this.clearStylesheets(theme);
    this.resolver && this.resolver(true);
  };

  /**
   * Creates a css link tag and sets its css file path to be loaded
   * @param {string} theme
   */
  loadStylesheet = (theme: string | undefined) => {
    const head = document.querySelector('head');
    let link = document.createElement('link');
    let href = `${Configuration.getPathToStaticFolder()}css/${theme}.css`;
    _.assign(link, {
      rel: 'stylesheet',
      type: 'text/css',
      href: href,
      media: 'all',
      onload: this.onCssLoaded.bind(this, link, theme),
      onerror: this.onCssLoaded.bind(this, link, theme)
    });
    if (head) {
      head.appendChild(link);
    }
  };

  public getThemeData = (theme: string) => {
    const themes: iThemeProviderProps = this.getThemes();
    if (!theme) {
      theme = themes[this.defaultThemeName].name;
    }
    let [previousTheme = null, previousHash = null] = this.previousTheme?.split('.') || [];
    let [currentTheme, currentHash] = theme?.split('.') || [];
    let sameThemeName: boolean = previousTheme === currentTheme;
    let sameThemeHash: boolean = previousHash === currentHash;
    let themeChanged: boolean = !sameThemeName || !sameThemeHash;
    if (!themes[currentTheme]) {
      // if non existing theme name is passed, do not change theme
      theme = themes[this.defaultThemeName].name;
      themeChanged = this.previousTheme !== theme;
    } else {
      theme = themes[currentTheme].name;
      // reassigning value in case that css hash has been changed in the meantime
    }

    if (!themeChanged && !this.initialized) {
      themeChanged = true;
    }
    if (themeChanged) {
      this.previousTheme = theme;
      if (theme) {
        CookieUtil.setTheme(theme);
      }
    }

    if (!this.initialized) {
      this.initialized = true;
    }

    return {
      themeChanged,
      theme
    };
  };

  /**
   * Changes the theme by loading a html link tag with css file path.
   * If no theme is provided then default css link will remain and theme loading promise will be resolved
   * @param {Function} resolve Promise resolver
   * @param {Function} reject Promise reject
   * @param {String} theme A theme name to load
   */
  public changeTheme = (resolve: Function, reject: Function, theme: string = CookieUtil.getTheme()) => {
    let { theme: newTheme, themeChanged } = this.getThemeData(theme);
    theme = newTheme;
    this.resolver = resolve;
    this.reject = reject;
    if (theme) {
      if (themeChanged) {
        this.loadStylesheet(theme);
      }
      resolve({ themeRejected: false });
    } else {
      resolve(true); // Resolving because there is already default theme loaded
    }
  };

  public forceChangeTheme = (resolve: Function, reject: Function, theme: string = CookieUtil.getTheme()) => {
    let { theme: newTheme, themeChanged } = this.getThemeData(theme);
    theme = newTheme;
    this.resolver = resolve;
    this.reject = reject;

    let isInitialThemeLoad = this.previousTheme === null;

    if (theme && themeChanged && isInitialThemeLoad) {
      this.loadStylesheet(theme);
    }
    resolve(true);
  };

  /**
   * After css link is loaded test whether css is loaded properly
   * and resolve theme loading promise or reject loading
   * @param {HTMLLinkElement} link Html element which loads css file
   * @param {string} theme A theme to load
   */
  onCssLoaded = (link: HTMLLinkElement, theme: string | undefined = undefined) => {
    // @ts-ignore
    const { cssRules = [] } = link.sheet;

    if (cssRules.length) {
      this.changeThemeResolve(theme);
    } else {
      CookieUtil.remove(COOKIE.THEME);
      this.reject && this.reject({ themeRejected: true });
    }
  };

  public setThemes = (themes: iThemeProviderProps) => {
    this.themes = themes;
  };

  public getThemes = (): iThemeProviderProps => this.themes;

  public isThemeServiceInitialized = (): boolean => this.initialized;
}

const ThemeServiceInstance = new ThemeService();
export default ThemeServiceInstance;
